import { useEffect, useState, type FormEvent } from 'react';
import { Button, CombatStatsBlock, Panel, Tabs, TextField, ViewHeader } from '@spacegame/ui';
import { UNITS, type UnitKey } from '@spacegame/engine';
import { useGame } from '../game/GameProvider';
import type { MessageCategory, MessageRecord } from '../game/backend-client';

const CATEGORIES: Array<{ id: MessageCategory; label: string }> = [
  { id: 'combat', label: 'Kampf' },
  { id: 'espionage', label: 'Spionage' },
  { id: 'private', label: 'Privat' },
];

function readableData(data: Record<string, unknown> | null): Array<[string, string]> {
  return Object.entries(data ?? {}).map(([key, value]) => [key, typeof value === 'object' ? JSON.stringify(value) : String(value)]);
}

function stringArray(value: unknown): string[] | null {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string') ? value as string[] : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function numericRecord(value: unknown): Record<string, number> | null {
  if (!isRecord(value)) return null;
  const entries = Object.entries(value).filter(
    (entry): entry is [string, number] => typeof entry[1] === 'number' && Number.isFinite(entry[1]),
  );
  return entries.length > 0 ? Object.fromEntries(entries) : null;
}

function UnitList({ title, units }: { title: string; units: Record<string, number> }) {
  return <div><h4 className="mb-1 font-display text-label uppercase text-content-muted">{title}</h4><ul className="space-y-0.5">{Object.entries(units).map(([key, count]) => <li key={key} className="flex justify-between text-xs"><span className="text-content-secondary">{UNITS[key as UnitKey]?.label ?? key}</span><span data-sg-numeric className="text-content">{count}</span></li>)}</ul></div>;
}

function ResourceBlock({ title, values }: { title: string; values: Record<string, number> }) {
  return <div><h4 className="mb-1 font-display text-label uppercase text-content-muted">{title}</h4><CombatStatsBlock dense stats={{ hull: values.titan ?? 0, shield: values.silicate ?? 0, damage: values.isotope ?? 0 }} /></div>;
}

function ReportData({ message }: { message: MessageRecord }) {
  const data = message.data;
  if (!data) return null;
  const garrison = numericRecord(data.garrison);
  const loot = numericRecord(data.loot);
  const losses = numericRecord(data.losses);
  const plundered = numericRecord(data.plundered);
  const debris = numericRecord(data.debris);
  const damaged = stringArray(data.damaged);

  if (message.category === 'espionage' && garrison && loot) {
    return <div className="space-y-3 rounded-control border border-edge-subtle bg-surface-2 p-3"><UnitList title="Garnison" units={garrison} /><ResourceBlock title="Beute" values={loot} /></div>;
  }
  if (message.category === 'espionage' && data.probes !== undefined && !garrison) {
    return <p className="rounded-control border border-warning/30 bg-warning/10 p-3 text-xs text-warning-bright">Die Spionagesonden wurden entdeckt und zerstört. Verlorene Sonden: {String(data.probes)}.</p>;
  }
  if (message.category === 'combat' && (losses || loot || plundered || debris)) {
    const resources = loot ?? plundered ?? debris;
    return <div className="space-y-3 rounded-control border border-edge-subtle bg-surface-2 p-3">{data.won !== undefined ? <p className={data.won ? 'text-xs text-success' : 'text-xs text-danger-bright'}>{data.won ? 'Sieg' : 'Niederlage'}</p> : null}{resources ? <ResourceBlock title={loot || plundered ? 'Beute' : 'Trümmer'} values={resources} /> : null}{losses ? <UnitList title="Verluste" units={losses} /> : null}</div>;
  }
  if (damaged) {
    return <div className="rounded-control border border-warning/30 bg-warning/10 p-3 text-xs text-warning-bright"><strong>Beschädigte Gebäude:</strong> {damaged.length > 0 ? damaged.join(', ') : 'keine'}</div>;
  }
  return <div className="space-y-1">{readableData(data).map(([key, value]) => <div key={key} className="flex justify-between gap-3 border-b border-edge-subtle py-1"><span className="text-content-muted">{key}</span><span className="text-right text-content">{value}</span></div>)}</div>;
}

export function MessagesView() {
  const { client, refreshUnreadMessages } = useGame();
  const [category, setCategory] = useState<MessageCategory>('combat');
  const [messages, setMessages] = useState<MessageRecord[]>([]);
  const [selected, setSelected] = useState<MessageRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recipientName, setRecipientName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      setMessages(await client.listMessages(category));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nachrichten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [category, client]);

  const openMessage = async (message: MessageRecord) => {
    setSelected({ ...message, read: true });
    if (!message.read) {
      await client.markMessageRead(message.id);
      setMessages((current) => current.map((entry) => entry.id === message.id ? { ...entry, read: true } : entry));
      await refreshUnreadMessages();
    }
  };

  const send = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await client.sendPrivateMessage(recipientName, subject, body);
      setRecipientName(''); setSubject(''); setBody('');
      setError(null);
      await refreshUnreadMessages();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nachricht konnte nicht gesendet werden.');
    }
  };

  const attackFromReport = (message: MessageRecord) => {
    const target = message.data?.target;
    if (!isRecord(target) || typeof target.ring !== 'number' || typeof target.system !== 'number' || typeof target.position !== 'number') return;
    window.dispatchEvent(new CustomEvent('spacegame:navigate-galaxy', { detail: { target } }));
  };

  return (
    <div className="space-y-3">
      <ViewHeader eyebrow="Postfach" title="Nachrichten" description="Kampfberichte, Spionageberichte und private Nachrichten." />
      <Tabs variant="enclosed" items={CATEGORIES} value={category} onChange={(id) => setCategory(id as MessageCategory)} />
      {error ? <p className="rounded-control border border-danger/40 bg-danger/10 p-3 text-sm text-danger-bright">{error}</p> : null}
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(18rem,0.8fr)]">
        <Panel title={loading ? 'Lade Nachrichten …' : 'Posteingang'} eyebrow={`${messages.length} Nachrichten`}>
          <div className="divide-y divide-edge-subtle">
            {messages.length === 0 && !loading ? <p className="py-5 text-sm text-content-muted">Keine Nachrichten in dieser Kategorie.</p> : null}
            {messages.map((message) => (
              <button key={message.id} type="button" onClick={() => void openMessage(message)} className="flex w-full items-start justify-between gap-3 py-3 text-left hover:bg-surface-2">
                <span className={message.read ? 'min-w-0' : 'min-w-0 font-semibold text-content'}>
                  {!message.read ? <span className="mr-2 inline-block h-2 w-2 rounded-full bg-accent" /> : null}
                  <span className="block truncate">{message.subject}</span>
                  <span className="block text-xs text-content-muted">{message.senderName ?? 'System'}</span>
                </span>
                <time className="shrink-0 text-xs text-content-muted">{new Date(message.createdAt).toLocaleString('de-DE')}</time>
              </button>
            ))}
          </div>
        </Panel>
        <div className="space-y-3">
          <Panel title={selected?.subject ?? 'Nachricht öffnen'} eyebrow={selected ? (selected.senderName ?? 'System') : 'Detail'}>
            {selected ? (
              <div className="space-y-3 text-sm">
                {selected.body ? <p className="whitespace-pre-wrap text-content-secondary">{selected.body}</p> : null}
                <ReportData message={selected} />
                {selected.category === 'espionage' && selected.data?.ships && selected.data?.target ? (
                  <Button variant="danger" onClick={() => attackFromReport(selected)}>Von hier angreifen</Button>
                ) : null}
              </div>
            ) : <p className="text-sm text-content-muted">Wähle eine Nachricht aus der Liste.</p>}
          </Panel>
          {category === 'private' ? (
            <Panel title="Neue Nachricht" eyebrow="Privat">
              <form className="space-y-2" onSubmit={(event) => void send(event)}>
                <TextField label="Empfänger" value={recipientName} onChange={setRecipientName} placeholder="Spielername" />
                <TextField label="Betreff" value={subject} onChange={setSubject} maxLength={80} />
                <label className="block text-xs uppercase text-content-muted">Nachricht<textarea className="mt-1 min-h-24 w-full rounded-control border border-edge bg-surface-2 p-2 text-sm text-content" value={body} onChange={(event) => setBody(event.target.value)} maxLength={2000} /></label>
                <Button type="submit">Senden</Button>
              </form>
            </Panel>
          ) : null}
        </div>
      </div>
    </div>
  );
}
